export class ArrayUtil {
  public static uniqWith<T>(array: T[], key: keyof T, inOppositeDirection = false) {
    const set = new Set()
    const result: T[] = []

    function push(item: T) {
      const value = item[key]

      if (!set.has(value)) {
        result.push(item)

        set.add(value)
      }
    }

    if (inOppositeDirection) {
      for (let i = array.length - 1; i >= 0; i--) {
        push(array[i])
      }

      result.reverse()
    } else {
      for (let i = 0; i < array.length; i++) {
        push(array[i])
      }
    }

    return result
  }

  public static calcPlaceholderNum(length: number, numPerGroup: number) {
    const remainder = length % numPerGroup

    return remainder && numPerGroup - remainder
  }

  static removeByIndex<T>(arr: T[], index: number) {
    if (index < 0) {
      return false
    }
    arr.splice(index, 1)
    return true
  }

  static remove<T>(arr: T[], item: T) {
    const idx = arr.indexOf(item)
    if (idx !== -1) {
      this.removeByIndex(arr, idx)
      return true
    } else {
      return false
    }
  }

  static clean<T>(arr: T[]) {
    arr.splice(0, arr.length)
    return arr
  }

  /**
   * change position of item
   * @param {*} arr list
   * @param {*} item item
   * @param {number} offsetIndex 1就向后调整一个， -1 就向前调整,超出范围不做调整
   */
  static changePosition<T>(arr: T[], item: T, offsetIndex: number) {
    const idx = arr.indexOf(item)
    if (idx !== -1) {
      const newIndex = idx + offsetIndex
      if (newIndex >= 0 && newIndex < arr.length) {
        arr.splice(idx, 1)
        arr.splice(newIndex, 0, item)
        return true
      }
    }
    return false
  }

  static changePosition2First<T>(arr: T[], item: T) {
    const idx = arr.indexOf(item)
    return this.changePosition(arr, item, idx * -1)
  }

  static changePosition2Last<T>(arr: T[], item: T) {
    const idx = arr.indexOf(item)
    return this.changePosition(arr, item, arr.length - idx - 1)
  }

  static equal<T>(arr1: T[], arr2: T[]) {
    if (arr1.length !== arr2.length) {
      return false
    }

    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) {
        return false
      }
    }

    return true
  }

  static random<T>(arr: T[]) {
    return arr[Math.floor(Math.random() * arr.length)]
  }

  static randomSort<T>(arr: T[]) {
    for (let i = 0; i < arr.length; i++) {
      const j = Math.floor(Math.random() * arr.length)
      const temp = arr[i]
      arr[i] = arr[j]
      arr[j] = temp
    }

    return arr
  }

  static randomNumber(min = 0, max = 1) {
    return Math.floor(Math.random() * (max - min)) + min
  }

  static buildIdMap<T>(ids: string[], initialValue: T = true as any) {
    return ids.reduce((result: { [index: string]: T }, id) => {
      result[id] = initialValue
      return result
    }, {})
  }

  static number2Array(n: number, start = 1, step = 1) {
    const result: number[] = []

    for (let i = 0, k = 0; k < n; k++, i += step) {
      result.push(start + i)
    }

    return result
  }

  static buildMapByKey<T>(arr: T[], key: keyof T) {
    return arr.reduce((result: { [index: string]: T }, item) => {
      result[item[key] as any] = item
      return result
    }, {})
  }

  static has<T>(_arr: T[], _i: number) {
    return _i >= 0 && _i < _arr.length
  }

  static swap<T>(_arr: T[], _i: number, _j: number) {
    if (this.has(_arr, _i) && this.has(_arr, _j)) {
      const tmp = _arr[_i]
      _arr[_i] = _arr[_j]
      _arr[_j] = tmp
    }
  }

  static last<T>(_arr: T[], _filter?: (_v: T) => boolean) {
    if (_filter) {
      for (let i = _arr.length - 1; i >= 0; i--) {
        if (_filter(_arr[i])) {
          return _arr[i]
        }
      }

      return
    }

    return _arr[_arr.length - 1]
  }

  static join<T>(arr: T[], separator = '', max = 0, postfix = '') {
    if (max <= 0 || arr.length <= max) {
      return arr.join(separator)
    }

    return `${arr.slice(0, max).join(separator)}等${arr.length}${postfix}`
  }

  static trim<T>(_arr: T[], _filter?: (_v: T) => boolean) {
    this.trimLeft(_arr, _filter)
    this.trimRight(_arr, _filter)

    return _arr
  }

  static trimLeft<T>(_arr: T[], _filter?: (_v: T) => boolean) {
    const filter =
      _filter ||
      function (_v: T) {
        return !!_v
      }

    const idx = _arr.findIndex(filter)

    _arr.splice(0, idx >= 0 ? idx : _arr.length)

    return _arr
  }

  static minByKey<T>(arr: T[], key: keyof T) {
    let item = arr[0]

    for (let i = 0; i < arr.length; i++) {
      if (arr[i][key] < item[key]) {
        item = arr[i]
      }
    }

    return item
  }

  static maxByKey<T>(arr: T[], key: keyof T) {
    let item = arr[0]

    for (let i = 0; i < arr.length; i++) {
      if (arr[i][key] > item[key]) {
        item = arr[i]
      }
    }

    return item
  }

  static avgByKey<T>(arr: T[], key: keyof T) {
    return this.sumByKey(arr, key) / (arr.length || 1)
  }

  static sumByKey<T>(arr: T[], key: keyof T) {
    return arr.reduce((result: number, item) => {
      result += item[key] as number
      return result
    }, 0)
  }

  static trimRight<T>(_arr: T[], _filter?: (_v: T) => boolean) {
    const filter =
      _filter ||
      function (_v: T) {
        return !!_v
      }

    let idx = -1
    for (let i = _arr.length - 1; i >= 0; i--) {
      if (filter(_arr[i])) {
        idx = i
        break
      }
    }

    _arr.splice(idx + 1, _arr.length - idx - 1)

    return _arr
  }

  static attachIdx<T>(_arr: T[]) {
    return _arr.map((_item, _idx) => ({
      ..._item,
      idx: _idx,
    }))
  }

  static adjustIdx<T>(_arr: { length: number }, _idx: number) {
    if (_idx < 0) {
      return _arr.length - 1
    }
    if (_idx >= _arr.length) {
      return 0
    }

    return _idx
  }

  static groupByKey<T>(_arr: T[], _key: keyof T) {
    const result: Record<string, T[]> = {}

    _arr.forEach((_item) => {
      const key = _item[_key] as string

      if (!result[key]) {
        result[key] = []
      }

      result[key].push(_item)
    })

    return result
  }

  /**
   *
   * @param _arr [[1, 2], [2, 3]]
   * @param needSort
   * @returns [1, 3]
   */
  static mergeRanges(_arr: [number, number][], needSort = true) {
    const arr = [..._arr]

    if (arr.length) {
      const result: [number, number][] = []

      if (needSort) {
        arr.sort((a1, a2) => {
          if (a1[0] === a2[0]) {
            return a1[0] - a2[0]
          } else {
            return a1[1] - a2[1]
          }
        })
      }

      let range = arr[0]
      for (let i = 1; i < arr.length; i++) {
        if (range[1] < arr[i][0]) {
          result.push(range)
          range = arr[i]
        } else {
          range[1] = arr[i][1]
        }
      }

      result.push(range)

      return result
    }

    return arr
  }

  /**
   * 蛇形展示顺序：按 _groupLength 分段，组号从 1 起，第 2、4、6… 段内逆序。不修改入参，返回新数组。
   */
  static snakeSort<T>(_list: T[], _groupLength = 3): T[] {
    if (_groupLength < 1) {
      return [..._list]
    }

    const out: T[] = []

    for (let i = 0; i < _list.length; i += _groupLength) {
      const chunk = _list.slice(i, i + _groupLength)
      const groupNo = Math.floor(i / _groupLength) + 1

      if (groupNo % 2 === 0) {
        chunk.reverse()
      }

      out.push(...chunk)
    }

    return out
  }
}
